import { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import DOMPurify from 'dompurify';
import parse from 'html-react-parser';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { pickLocalized } from '../components/RichContentModal';
import { ContentArticle, ContentContainer } from '../components/ContentContainer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const DynamicContentPage = () => {
  const { linkId } = useParams();
  const { t, i18n } = useTranslation();
  const [link, setLink] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    axios.get(`${API}/footer`).then(res => {
      const sections = res.data || [];
      for (const s of sections) {
        const found = (s.links || []).find(l => l.link_id === linkId);
        if (found) {
          setLink(found);
          return;
        }
      }
      setNotFound(true);
    }).catch(() => setNotFound(true));
  }, [linkId]);

  const lang = i18n.language || 'fr';
  const title = link ? pickLocalized(link.title_i18n, lang) : '';
  const html = link ? pickLocalized(link.content_i18n, lang) : '';
  const sanitized = useMemo(
    () => (html ? DOMPurify.sanitize(html, { USE_PROFILES: { html: true } }) : ''),
    [html]
  );

  if (notFound) {
    return (
      <div className="min-h-screen bg-page">
        <Header />
        <ContentContainer variant="prose" testId="dynamic-content-notfound">
          <div className="text-center py-20">
            <p className="text-lg text-text_muted">{t('common.pageNotFound') || 'Page not found'}</p>
            <Link to="/" className="text-primary hover:underline mt-4 inline-block">
              ← {t('common.backHome') || 'Back home'}
            </Link>
          </div>
        </ContentContainer>
        <Footer />
      </div>
    );
  }
  if (!link) {
    return (
      <div className="min-h-screen bg-page">
        <Header />
        <ContentContainer variant="prose" testId="dynamic-content-loading">
          <div className="text-center py-20">
            <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        </ContentContainer>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-page">
      <Header />
      <ContentArticle title={title} testId="dynamic-content">
        {sanitized
          ? parse(sanitized)
          : <p className="text-text_muted">{t('common.noContent') || 'No content yet.'}</p>}
      </ContentArticle>
      <Footer />
    </div>
  );
};
